import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Paper,
    IconButton,
    Tooltip,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
} from "@mui/material";
import AddProductFamilyForm from "./AddProductFamilyForm";
import DeleteOutlineTwoToneIcon from "@mui/icons-material/DeleteOutlineTwoTone";
import UpdateProductFamilyForm from "./UpdateProductFamilyForm";
import EditTwoToneIcon from "@mui/icons-material/EditTwoTone";
import translations from "../translations/productFamilyTranslation";
import { isTestEnv } from "../globals";

const ProductFamily = ({ rolesList, setMockDropdownData, setSnackbar, execute }) => {
    const [productFamilies, setProductFamilies] = useState([]);
    const [editProductFamilyId, setEditProductFamilyId] = useState(null);
    
    useEffect(() => {
        const getData = async () => {
            try {
                const familyResponse = await execute(
                    "GET",
                    `${process.env.REACT_APP_BACKEND_BASE_URL}/api/productFamily/getinfrastructuresProductFamilys`
                );
                setProductFamilies(familyResponse.data);
            } catch (err) {
                isTestEnv && console.error(34,err);
            }
        };
        getData();
    }, []);

    const handleDeleteProductFamily = async (pfId) => {
        const isSure = window.confirm(translations.deleteProductFamilyConfirm);
        if (isSure) {
            try {
                await execute(
                    "PATCH",
                    `${process.env.REACT_APP_BACKEND_BASE_URL}/api/productFamily/updateProductFamily/`,
                    { _id: pfId, infrastructures: false, orderApprovalRequired: false, approverRole: null }
                );
                setProductFamilies(productFamilies.filter((pf) => pf._id !== pfId));
                setMockDropdownData((prev) => ({ ...prev, family: productFamilies.filter((pf) => pf._id !== pfId) }));
                setSnackbar({
                    open: true,
                    message: translations.productFamilyDeletedSuccess,
                    severity: 'success'
                });
            } catch (error) {
                isTestEnv && console.error(error);
                setSnackbar({
                    open: true,
                    message: translations.error,
                    severity: 'error'
                });
            }
        }
    };

    const handleSave = async (updatedProductFamily) => {
        updatedProductFamily = {
            ...updatedProductFamily,
            approverRole: updatedProductFamily.orderApprovalRequired ? updatedProductFamily.approverRole : null
        };
        try {
            await execute(
                "PATCH",
                `${process.env.REACT_APP_BACKEND_BASE_URL}/api/productFamily/updateProductFamily/`,
                { ...updatedProductFamily }
            );
            setProductFamilies(prevFamilies =>
                prevFamilies.map(pf =>
                    pf._id === updatedProductFamily._id ? updatedProductFamily : pf
                )
            );
            setSnackbar({
                open: true,
                message: translations.productFamilyUpdateSuccess,
                severity: 'success'
            });
        } catch (err) {
            isTestEnv && console.error(err);
            setSnackbar({
                open: true,
                message: translations.productFamilyUpdateError,
                severity: 'error'
            });
        }
        setEditProductFamilyId(null);
    };

    const handleCancel = () => {
        setEditProductFamilyId(null);
    };

    return (
        <div>
            <Box mt={2}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 3,
                    }}
                >
                    <Typography variant="subtitle1">
                        {translations.productFamilies}
                    </Typography>
                    <AddProductFamilyForm
                        rolesList={rolesList}
                        setProductFamilies={setProductFamilies}
                        setMockDropdownData={setMockDropdownData}
                    />
                </Box>

                <TableContainer component={Paper}>
                    <Table aria-label="product families table">
                        <TableHead>
                            <TableRow>
                                <TableCell>{translations.name}</TableCell>
                                <TableCell>{translations.description}</TableCell>
                                <TableCell>{translations.familyType}</TableCell>
                                <TableCell>{translations.orderApprovalRequired}</TableCell>
                                <TableCell>{translations.approvalGroup}</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {productFamilies.map((pf) =>
                                editProductFamilyId === pf._id ? (
                                    <UpdateProductFamilyForm
                                        key={pf._id}
                                        pf={pf}
                                        rolesList={rolesList}
                                        onSave={handleSave}
                                        onCancel={handleCancel}
                                    />
                                ) : (
                                    <TableRow key={pf._id}>
                                        <TableCell>{pf.familyName}</TableCell>
                                        <TableCell>{pf.familyDes}</TableCell>
                                        <TableCell>{pf.ft}</TableCell>
                                        <TableCell>
                                            {pf.orderApprovalRequired ? translations.yes : translations.no}
                                        </TableCell>
                                        <TableCell>{pf.approverRole?.Name}</TableCell>
                                        <TableCell>
                                            <Tooltip title={translations.edit}>
                                                <IconButton
                                                    onClick={() => setEditProductFamilyId(pf._id)}
                                                    color="primary"
                                                >
                                                    <EditTwoToneIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={translations.deleteProductFamily}>
                                                <IconButton
                                                    color="warning"
                                                    aria-label="delete product family"
                                                    onClick={() => handleDeleteProductFamily(pf._id)}
                                                >
                                                    <DeleteOutlineTwoToneIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                )
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </div>
    );
};

export default ProductFamily;